import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import type { Trope, WorldSetting } from "@/types/summary";

export interface FactSheetWithoutCharacters {
  title: string;
  author: string;
  synopsis: string;
  manuscript_word_count?: number;
  genres?: string[];
  world_setting?: WorldSetting;
  tropes?: Trope[];
}

export interface UseSummaryResult {
  factSheetWithoutCharacters: FactSheetWithoutCharacters | null;
  isLoading: boolean;
  error: Error | null;
}

export const useSummary = (summaryId: string | null): UseSummaryResult => {
  const { authUser } = useAuth();
  const [factSheetWithoutCharacters, setFactSheetWithoutCharacters] =
    useState<FactSheetWithoutCharacters | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!authUser || !summaryId) {
      setFactSheetWithoutCharacters(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);

    const summaryDocRef = doc(db, "summaries", summaryId);

    const unsubscribe = onSnapshot(
      summaryDocRef,
      (summaryDoc) => {
        if (!summaryDoc.exists()) {
          setFactSheetWithoutCharacters(null);
          setIsLoading(false);
          return;
        }

        const data = summaryDoc.data();

        setFactSheetWithoutCharacters({
          title: data.manuscript?.metadata?.title || "Unknown",
          author: data.manuscript?.metadata?.author || "Unknown",
          synopsis: data.synopsis || "",
          manuscript_word_count: data.manuscript?.word_count,
          genres: data.genres,
          world_setting: data.world_setting,
          tropes: data.tropes,
        });
        setIsLoading(false);
      },
      (err) => {
        logError(err);
        setError(err);
        setFactSheetWithoutCharacters(null);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser, summaryId]);

  return { factSheetWithoutCharacters, isLoading, error };
};
